import crypto from 'crypto'
import { Client as GridPlusSDKClient, Constants as GridPlusSDKConstants, Utils } from 'gridplus-sdk'
import { Hash } from 'ox'

import { RLP } from '@ethereumjs/rlp'
import { translation } from '@web/utils/safeTranslation'

import {
  requestLatticeDeviceId,
  requestLatticePairingSecret
} from './latticePromptBridge'

// Phase 4 (keystore isolation) — GridPlus / Lattice1 on mobile-web.
//
// `MainController` (and the `LatticeSigner` it routes signing through) runs inside
// a Web Worker, which has no DOM and no `window.open`. The extension's
// `LatticeController` connects by opening the GridPlus Lattice Manager in a tab
// (`browser.tabs.create`) and polling that tab's CROSS-ORIGIN url for a
// `&loginCache=` credential blob — a privileged read that only works with the
// `browser.tabs` extension API. A plain SPA cannot read a cross-origin popup url,
// so that connector flow is impossible here.
//
// Instead this page-side controller uses the gridplus-sdk's native pairing flow:
//   1. `connect(deviceId)`  — contacts the Lattice; resolves whether it is paired.
//   2. if unpaired, the Lattice screen shows a one-time pairing secret which the
//      user types in; `pair(secret)` finalizes the pairing.
//   3. `getAddresses` / `sign` proceed against the now-active wallet.
//
// The worker holds a thin proxy (`latticeWorkerProxy.ts`) whose method calls
// round-trip here over `postMessage` (see `WebViewWorker.web.tsx`
// `hwLatticeInvoke`). Only non-secret data crosses: derived public addresses,
// unsigned payloads and returned signatures. The decrypted keystore
// `#mainKey`/seed never leave the worker, and every device operation requires
// physical on-device confirmation, so running the signer transport on the page
// does not widen the keystore-isolation attack surface.

const LATTICE_APP_NAME = 'ZKX Wallet (web)' // must be 6-23 characters
const LATTICE_BASE_URL = 'https://signing.gridpl.us'

const SDK_TIMEOUT = 120000
const CONNECT_TIMEOUT = 20000

// The client private key is this app's pairing identity to the Lattice. It is NOT
// the wallet seed and grants no signing power on its own — every signature still
// needs physical confirmation on the device. Persisting it (alongside the device
// id) lets a paired Lattice be reused across reloads without re-pairing.
const STORAGE_KEY = 'zkx:lattice:client:v1'

type PersistedState = { privKey: string; deviceId: string }

type FwVersion = { major: number; minor: number; fix: number }

type LatticeUnlockResult = {
  status: 'ALREADY_UNLOCKED' | 'JUST_UNLOCKED'
  fwVersion: FwVersion | null
  uid: string
}

class LatticePageController {
  walletSDK: GridPlusSDKClient | null = null

  deviceId = ''

  #privKey: Buffer | null = null

  constructor() {
    this.#loadPersisted()
  }

  #loadPersisted() {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY)
      if (!raw) return
      const parsed: PersistedState = JSON.parse(raw)
      if (parsed?.privKey) this.#privKey = Buffer.from(parsed.privKey, 'hex')
      if (parsed?.deviceId) this.deviceId = parsed.deviceId
    } catch {
      // Corrupt blob — ignore and re-pair from scratch.
    }
  }

  #persist() {
    if (!this.#privKey) return
    const state: PersistedState = {
      privKey: this.#privKey.toString('hex'),
      deviceId: this.deviceId
    }
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
  }

  #ensurePrivKey(): Buffer {
    if (this.#privKey) return this.#privKey
    const buf = Buffer.alloc(32)
    crypto.randomFillSync(buf)
    this.#privKey = buf
    return buf
  }

  // Credential input is collected by the styled `LatticePairingModal` via
  // `latticePromptBridge`, keeping the connect/pair logic and worker bridge free
  // of any UI concern.
  // eslint-disable-next-line class-methods-use-this
  async #promptDeviceId(): Promise<string> {
    const value = (await requestLatticeDeviceId()).trim()
    if (!value) throw new Error(translation.t('Connecting to the Lattice1 was cancelled.'))
    return value
  }

  // eslint-disable-next-line class-methods-use-this
  async #promptPairingSecret(): Promise<string> {
    const value = (await requestLatticePairingSecret()).trim()
    if (!value) throw new Error(translation.t('Pairing with the Lattice1 was cancelled.'))
    return value
  }

  // Lazily (re)establishes a paired session with the Lattice. Safe to call before
  // every device operation — once connected and paired it returns fast.
  async #ensureConnected(): Promise<void> {
    const privKey = this.#ensurePrivKey()

    if (!this.deviceId) {
      this.deviceId = await this.#promptDeviceId()
    }

    if (!this.walletSDK) {
      this.walletSDK = new GridPlusSDKClient({
        name: LATTICE_APP_NAME,
        baseUrl: LATTICE_BASE_URL,
        privKey,
        timeout: SDK_TIMEOUT,
        skipRetryOnWrongWallet: true
      })
    }

    // Shorter timeout for the initial contact so an unplugged/offline device
    // fails fast instead of hanging for the full SDK timeout.
    this.walletSDK.timeout = CONNECT_TIMEOUT
    let isPaired: boolean
    try {
      isPaired = await this.walletSDK.connect(this.deviceId)
    } finally {
      this.walletSDK.timeout = SDK_TIMEOUT
    }

    if (!isPaired) {
      // The Lattice now shows a one-time pairing secret on its screen.
      const secret = await this.#promptPairingSecret()
      await this.walletSDK.pair(secret)
    }

    // Persist only after a confirmed connection so we never cache a device id the
    // user typo'd before they could correct it.
    this.#persist()
  }

  #getFwVersion(): FwVersion | null {
    try {
      return this.walletSDK ? this.walletSDK.getFwVersion() : null
    } catch {
      return null
    }
  }

  #getCurrentWalletUID(): string {
    if (!this.walletSDK) return ''
    const activeWallet = this.walletSDK.getActiveWallet()
    if (!activeWallet || !activeWallet.uid) return ''
    return activeWallet.uid.toString('hex')
  }

  async unlock(): Promise<LatticeUnlockResult> {
    const wasConnected = !!this.walletSDK && !!this.#getCurrentWalletUID()
    await this.#ensureConnected()
    return {
      status: wasConnected ? 'ALREADY_UNLOCKED' : 'JUST_UNLOCKED',
      fwVersion: this.#getFwVersion(),
      uid: this.#getCurrentWalletUID()
    }
  }

  async getAddresses(keyData: any): Promise<string[]> {
    await this.#ensureConnected()
    return this.walletSDK!.getAddresses(keyData) as Promise<string[]>
  }

  async sign(req: any): Promise<any> {
    await this.#ensureConnected()
    return this.walletSDK!.sign(req)
  }

  // Mirrors `LatticeController._keyIdxInCurrentWallet`: the signer uses this to
  // confirm the key it is about to sign with belongs to the device's active
  // wallet (guards against the user swapping SafeCards/seeds mid-session).
  async keyIdxInCurrentWallet(key: any): Promise<number | null> {
    await this.#ensureConnected()
    const activeUID = this.#getCurrentWalletUID()
    if (key?.meta?.deviceId === activeUID) return key.meta.index
    return null
  }

  // Copied from `LatticeController.signAuthorization` (EIP-7702). Kept on the page
  // because it calls the real `walletSDK.sign`; the worker proxy RPCs the whole
  // method here so the signer's `sign7702` is unchanged.
  async signAuthorization({
    contract,
    chainId,
    nonce,
    signerPath
  }: {
    contract: string
    chainId: bigint
    nonce: bigint
    signerPath: number[]
  }): Promise<any> {
    await this.#ensureConnected()
    if (!this.walletSDK) throw new Error(translation.t('Lattice not connected'))

    const MAGIC = Buffer.from([0x05]) // EIP-7702 magic byte
    const message = Buffer.concat([MAGIC, Buffer.from(RLP.encode([chainId, contract, nonce]))])
    const payload = {
      signerPath,
      curveType: GridPlusSDKConstants.SIGNING.CURVES.SECP256K1,
      hashType: GridPlusSDKConstants.SIGNING.HASHES.KECCAK256,
      encodingType: GridPlusSDKConstants.SIGNING.ENCODINGS.EIP7702_AUTH,
      payload: message
    }

    const response = await this.walletSDK.sign({ data: payload })

    if (response.sig && (response as any).pubkey) {
      const messageHash = Buffer.from(Hash.keccak256(message))
      const yParity = Utils.getYParity(messageHash, response.sig, (response as any).pubkey)

      const rValue = Buffer.isBuffer(response.sig.r)
        ? `0x${response.sig.r.toString('hex')}`
        : response.sig.r
      const sValue = Buffer.isBuffer(response.sig.s)
        ? `0x${response.sig.s.toString('hex')}`
        : response.sig.s

      return {
        address: contract,
        chainId,
        nonce,
        yParity: `0x${yParity.toString(16)}`,
        r: rValue,
        s: sValue,
        messageHash
      }
    }

    throw new Error(
      translation.t(
        'Problem occurred when trying to create the signature components for the EIP-7702 authorization.'
      )
    )
  }

  cleanUp() {
    // The session/pairing is intentionally kept so the user does not have to
    // re-enter the pairing code on every signing request.
  }
}

export default LatticePageController
